import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import Button from "../../Generic/Button";
import Card from "../../Generic/Card";
import InputBox from "../../Generic/InputBox";
import SuccessButton from "../../Generic/SuccessButton";
import Textarea from "../../Generic/Textarea";
import CookBookConnector from "../Connector/CookBookConnector";
import { successNotification, errorNotification } from "../../Generic/Notifications";
import { deepCopy } from "../../util/util";
import { CSSTransition } from "react-transition-group";
import close from "../../Assets/Close.png";
import { useNavigate, useParams } from "react-router-dom";
import { DispenseType, dispenseTypes, DispensePatternType, dispensePatternTypes } from "../Reducer";
import Checkbox from "../../Generic/Checkbox";
import Dropdown from "../../Generic/Dropdown";
import StudioService from "../Services/StudioService";
import DecimalInput from "../../Generic/NumberInput";
import { zIndex } from "../../config/zIndex";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1rem;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	height: ${(props) => props.height};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	overflow-y: ${(props) => props.overflowY};
	gap: ${(props) => props.gap};
	border: ${(props) => props.border};
	cursor: ${(props) => props.cursor};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
	${(props) =>
		props.active &&
		css`
			background: rgba(45, 35, 66, 0.8);
			color: #fff;
		`}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.modalBackdrop};
`;

const Modal = styled.div`
	display: flex;
	flex-direction: column;
	background: #ffffff;
	max-height: 90%;
	height: max-content;
	width: 60vw;
	overflow-x: hidden;
	border-radius: 10px;
	z-index: ${zIndex.modal};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		width: ${(props) => props.widthBefore1000};
	}
`;
// Column not used in this file; prefer direct Div with flexDirection

// const FieldColumn = styled(Column)`
// 	/* Column container for a label + input */
// `;

// Reusable layout pieces to match IngredientCreate
const SectionHeader = styled(Div)`
	width: 100%;
	font-size: 1.4rem;
	font-weight: 600;
	margin-top: 1rem;
`;

const Label = styled(Span)`
	color: rgba(0, 0, 0, 0.6);
`;

const AllergenChip = styled(Div)`
	cursor: pointer;
	font-size: 14px;
	padding: 0.5rem;
	border: 1px solid rgba(0, 0, 0, 0.4);
`;

const DefaultInput = (props) => {
	const { width, breakpoint, widthBeforeBreakpoint, ...rest } = props;
	return <InputBox padding="0.5rem" margin="0.5rem 0 0 0" width={"20rem"} {...rest} />;
};

const DefaultDecimalInput = (props) => {
	const { width, breakpoint, widthBeforeBreakpoint, ...rest } = props;
	return <DecimalInput padding="0.5rem" margin="0.5rem 0 0 0" width={"20rem"} {...rest} />;
};

const DefaultDropdown = (props) => {
	return (
		<Dropdown
			padding="0.5rem"
			headerFontSize="1rem"
			boxShadow=""
			border="1px solid rgba(0,0,0,0.6)"
			margin="0.5rem 0 0 0"
			{...props}
		/>
	);
};

const IngredientEdit = (props) => {
	const { id } = useParams();
	const { objectToEdit, HeatingProfiles, DispenserTypes, Allergens, containerTypes, baseTypes, spoutTypes } = props;
	const { changeEditMode } = props;
	const navigate = useNavigate();
	const [confirmModal, showConfirmModal] = useState(false);

	useEffect(() => {
		return () => {
			changeEditMode({ objectToEdit: {} });
		};
	}, [changeEditMode]);

	function updateData(evnt, array) {
		//Get numeric input for fields if type is set as number
		const payload =
			evnt.target.type === "number"
				? { column: evnt.target.id, value: evnt.target.valueAsNumber, isArray: array }
				: { column: evnt.target.id, value: evnt.target.value, isArray: array };

		props.updateIngredient(payload);
	}

	function customUpdateEvent(key, value) {
		props.updateIngredient({ column: key, value });
	}

	function toggleAllergen(allergen) {
		let newAllergens = objectToEdit?.dietaryDetails?.allergens ? deepCopy(objectToEdit.dietaryDetails.allergens) : [];
		if (objectToEdit?.dietaryDetails?.allergens?.includes(allergen)) {
			newAllergens = newAllergens.filter((selectedAllergen) => selectedAllergen !== allergen);
		} else {
			newAllergens.push(allergen);
		}
		customUpdateEvent("dietaryDetails.allergens", newAllergens);
	}

	async function sendUpdatedData() {
		const response = await StudioService.updateIngredient(id, props.objectToEdit);
		const { data, error } = response;
		if (data) {
			showConfirmModal(false);
			successNotification("Changes saved successfully");
			navigate(`/studio/ingredient/${id}`);
		}
		if (error) {
			errorNotification(error);
		}
	}

	function renderIngredientEdit() {
		return (
			<Card gap="1.5rem" margin="2rem 0 0 0" width="100%" alignItem="center" flexDirection="column">
				<Div width="100%" alignItem="center">
					<Div margin="0" width="100%" flexDirection="row" jContent="space-between" alignItem="" gap="1.5rem">
						<Div flexDirection="column" jContent="space-between" width="100%" margin="0 0 0" gap="0.25rem">
							<Label>Name</Label>
							<DefaultInput id={"name"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.name} width="30rem" />
						</Div>
						<Div alignItem="flex-end" width="100%" margin="0 0 0.5rem 0">
							<Checkbox defaultChecked={objectToEdit?.isBaseItem} value={objectToEdit?.isBaseItem} onClick={(e) => customUpdateEvent("isBaseItem", e.target.checked)} />
							<Span margin="0 0 0 0.5rem">Base Item</Span>
						</Div>
					</Div>
				</Div>
				<Div width="100%" alignItem="center">
					<Div width="100%" margin="0" flexDirection="column" jContent="" alignItem="">
						<Label>Description</Label>
						<Div jContent="space-between" width="100%" margin="0.5rem 0 0 0">
							<Textarea id={"description"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.description} padding="0.5rem" width="100%" />
						</Div>
					</Div>
				</Div>
				<Div width="100%" alignItem="center">
					<Div width="100%" flexDirection="column">
						<Label>Image Url</Label>
						<DefaultInput margin="0.5rem 0 0 0" id={"imageUrl"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.imageUrl} width="100%" />
					</Div>
				</Div>
				<Div width="100%" alignItem="center" gap="1.5rem">
					<Div width="20rem" flexDirection="column">
						<Label>Heating Profile</Label>
						<DefaultDropdown optionsList={HeatingProfiles} onChange={(val) => customUpdateEvent("defaultHeatingProfile", val)} value={objectToEdit?.defaultHeatingProfile} />
					</Div>
				</Div>
				<SectionHeader padding="0">Expiry Time</SectionHeader>
				<Div gap="1.5rem" padding="0" width="100%" alignItem="center" flexWrap="wrap">
					<Div flexDirection="column" padding="0">
						<Label>Room</Label>
						<DefaultInput type="number" id={"expiresInMinutes.room"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.expiresInMinutes?.room} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Refrigerated</Label>
						<DefaultInput type="number" id={"expiresInMinutes.refrigerated"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.expiresInMinutes?.refrigerated} />
					</Div>
				</Div>
				<SectionHeader>Dietary Details</SectionHeader>
				<Div flexDirection="column" width="100%" alignItem="" gap="1.5rem">
					<Div flexDirection="column">
						<Label>Calories Per Gram</Label>
						<DefaultInput type="number" id={"dietaryDetails.caloriesPerGram"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.dietaryDetails?.caloriesPerGram} />
					</Div>
					<Div flexDirection="column">
						<Label>Protein Per Gram</Label>
						<DefaultDecimalInput min={0} id={"dietaryDetails.proteinPerGram"} onChange={(e) => customUpdateEvent("dietaryDetails.proteinPerGram",e)} defaultValue={objectToEdit?.dietaryDetails?.proteinPerGram} />
					</Div>
					<Div flexDirection="column">
						<Label>Fibre Per Gram</Label>
						<DefaultDecimalInput min={0} id={"dietaryDetails.fibrePerGram"} onChange={(e) => customUpdateEvent("dietaryDetails.fibrePerGram",e)} defaultValue={objectToEdit?.dietaryDetails?.fibrePerGram} />
					</Div>
					<Div flexDirection="column">
					<Label>Allergens</Label>
					<Div margin="0.5rem 0 0 0" flexDirection="row" width="100%" flexWrap="wrap" gap="1rem">
							{Allergens
								? Object.entries(Allergens).map(([allergenKey, allergenValue]) => {
										const isSelected = objectToEdit?.dietaryDetails?.allergens?.includes(allergenKey);
										return (
									<AllergenChip active={isSelected} onClick={() => toggleAllergen(allergenKey)}>
										{allergenValue}
									</AllergenChip>
										);
								  })
								: ""}
						</Div>
					</Div>
				</Div>
				<SectionHeader padding="0">Fill Details</SectionHeader>
				<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
					<Div flexDirection="column" padding="0">
						<Label>Fill</Label>
						<DefaultInput type="number" id={"inventoryData.fillDetails.fillAmount"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.fillDetails?.fillAmount} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Max Fill</Label>
						<DefaultInput type="number" id={"inventoryData.fillDetails.maxAmount"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.fillDetails?.maxAmount} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Wastage</Label>
						<DefaultInput type="number" id={"inventoryData.fillDetails.wasteAmount"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.fillDetails?.wasteAmount} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Inventory Threshold Percentage</Label>
						<DefaultDecimalInput defaultValue={objectToEdit?.inventoryData?.fillDetails?.inventoryThresholdPercentage} min={0} max={1} onChange={(value) => customUpdateEvent("inventoryData.fillDetails.inventoryThresholdPercentage", value)} />
					</Div>
				</Div>
				<SectionHeader padding="0">Dispensing Type</SectionHeader>
				<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
					<Div flexDirection="column" width="20rem">
						<Label>Dispense Type</Label>
						<DefaultDropdown id={"inventoryData.dispenseDetails.dispenseType"} optionsList={dispenseTypes} onChange={(val) => customUpdateEvent("inventoryData.dispenseDetails.dispenseType", val)} value={objectToEdit?.inventoryData?.dispenseDetails?.dispenseType} />
					</Div>
					<Div flexDirection="column" width="20rem">
						<Label>Dispense Pattern Type</Label>
						<DefaultDropdown id={"inventoryData.dispenseDetails.dispensePatternType"} optionsList={dispensePatternTypes} onChange={(val) => customUpdateEvent("inventoryData.dispenseDetails.dispensePatternType", val)} value={objectToEdit?.inventoryData?.dispenseDetails?.dispensePatternType} />
					</Div>
				</Div>
				{/* To and Fro Params */}
				{objectToEdit?.inventoryData?.dispenseDetails?.dispenseType === DispenseType.DISPENSE_WITH_FEEDBACK && objectToEdit?.inventoryData?.dispenseDetails?.dispensePatternType === DispensePatternType.TO_AND_FRO ? (
					<>
						<SectionHeader>To and Fro Dispense Parameters</SectionHeader>
						<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
						<Div flexDirection="column" padding="0">
							<Label>Forward Dispense Degrees</Label>
							<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.dispensingCycleForwardDegrees"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.dispensingCycleForwardDegrees} />
						</Div>
						<Div flexDirection="column" padding="0">
							<Label>Forward Dispense RPM</Label>
							<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.dispensingCycleForwardRpm"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.dispensingCycleForwardRpm} />
						</Div>
						<Div flexDirection="column" padding="0">
							<Label>Backward Dispense Degrees</Label>
							<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.dispensingCycleBackwardDegrees"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.dispensingCycleBackwardDegrees} />
						</Div>
						<Div flexDirection="column" padding="0">
							<Label>Backward Dispense RPM</Label>
							<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.dispensingCycleBackwardRpm"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.dispensingCycleBackwardRpm} />
						</Div>
						</Div>
					</>
				) : (
					""
				)}
				{objectToEdit?.inventoryData?.dispenseDetails?.dispenseType === DispenseType.EQUATION_BASED_DISPENSING && objectToEdit?.inventoryData?.dispenseDetails?.dispensePatternType === DispensePatternType.NO_PATTERN ? (
					<>
					<SectionHeader>Equation Parameters</SectionHeader>
					<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
						<Div flexDirection="column" padding="0">
							<Label>Constant</Label>
							<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.constant"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.constant} />
						</Div>
						<Div flexDirection="column" padding="0">
							<Label>Dispensing Rate</Label>
							<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.dispensingRate"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.dispensingRate} />
						</Div>
					</Div>
					</>
				) : (
					""
				)}
				<SectionHeader padding="0">Back Rotation Parameters</SectionHeader>
				<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
					<Div flexDirection="column" padding="0">
						<Label>Back Rotation</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.backRotation"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.backRotation} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Back Rotation RPM</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.dispenseConfig.backRotationRPM"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.dispenseConfig?.backRotationRPM} />
					</Div>
				</Div>
				<SectionHeader padding="0">Dispensing Parameters</SectionHeader>
				<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
					<Div flexDirection="column" padding="0">
						<Label>Prime Rotation Count</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.primeRotationCount"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.primeRotationCount} />
					</Div>
					<Div flexDirection="column">
						<Label>Accuracy Buffer</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.accuracyBuffer"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.accuracyBuffer} />
					</Div>
					<Div alignItem="flex-end" height="100%" margin="0 0 0 0">
						<Checkbox
							disabled={objectToEdit?.inventoryData?.dispenseDetails?.dispenseType === DispenseType.DISPENSE_WITH_FEEDBACK}
							defaultChecked={objectToEdit?.inventoryData?.dispenseDetails?.agitationRequired}
							onClick={(e) => customUpdateEvent("inventoryData.dispenseDetails.agitationRequired", e.target.checked)}
						/>
						<Span margin="0 0 0 0.5rem">Agitation Required</Span>
					</Div>
				</Div>
				<SectionHeader padding="0">RPM Constraints</SectionHeader>
				<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
					<Div flexDirection="column" padding="0">
						<Label>Min RPM</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.rpmConstraints.0"} onChange={(e) => updateData(e, true)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.rpmConstraints?.[0]} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Max RPM</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.rpmConstraints.1"} onChange={(e) => updateData(e, true)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.rpmConstraints?.[1]} />
					</Div>
				</Div>
				<SectionHeader padding="0">Landing Time</SectionHeader>
				<Div padding="0" width="100%" alignItem="center" flexWrap="wrap" gap="1.5rem">
					<Div flexDirection="column" padding="0">
						<Label>Floor 0</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.landingTime.floor0"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.landingTime?.floor0} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Floor 1</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.landingTime.floor1"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.landingTime?.floor1} />
					</Div>
					<Div flexDirection="column" padding="0">
						<Label>Floor 2</Label>
						<DefaultInput type="number" id={"inventoryData.dispenseDetails.landingTime.floor2"} onChange={(e) => updateData(e)} defaultValue={objectToEdit?.inventoryData?.dispenseDetails?.landingTime?.floor2} />
					</Div>
				</Div>
				<SectionHeader padding="0">Hardware Config</SectionHeader>
				<Div flexWrap="wrap" width="100%" alignItem="center" gap="1rem">
					<Div width="20rem" flexDirection="column">
						<Span color="rgba(0,0,0,0.6)">Dispenser Type</Span>
						<Dropdown
							padding="0.5rem"
							headerFontSize="1rem"
							boxShadow=""
							border="1px solid rgba(0,0,0,0.6)"
							optionsList={DispenserTypes}
							onChange={(val) => customUpdateEvent("inventoryData.dispenseDetails.dispenserConfig.dispenserType", val)}
							margin="0.5rem 0 0 0"
							value={objectToEdit?.inventoryData?.dispenseDetails?.dispenserConfig?.dispenserType}
						/>
					</Div>
					<Div width="20rem" flexDirection="column">
						<Span color="rgba(0,0,0,0.6)">Canister</Span>
						<Dropdown
							padding="0.5rem"
							headerFontSize="1rem"
							boxShadow=""
							border="1px solid rgba(0,0,0,0.6)"
							optionsList={containerTypes}
							onChange={(val) => customUpdateEvent("inventoryData.dispenseDetails.dispenserConfig.containerType", val)}
							margin="0.5rem 0 0 0"
							value={objectToEdit?.inventoryData?.dispenseDetails?.dispenserConfig?.containerType}
						/>
					</Div>
					<Div width="20rem" flexDirection="column">
						<Span color="rgba(0,0,0,0.6)">Base Block</Span>
						<Dropdown
							padding="0.5rem"
							headerFontSize="1rem"
							boxShadow=""
							border="1px solid rgba(0,0,0,0.6)"
							optionsList={baseTypes}
							onChange={(val) => customUpdateEvent("inventoryData.dispenseDetails.dispenserConfig.baseType", val)}
							margin="0.5rem 0 0 0"
							value={objectToEdit?.inventoryData?.dispenseDetails?.dispenserConfig?.baseType}
						/>
					</Div>
					<Div width="20rem" flexDirection="column">
						<Span color="rgba(0,0,0,0.6)">Drain Tray</Span>
						<Dropdown
							padding="0.5rem"
							headerFontSize="1rem"
							boxShadow=""
							border="1px solid rgba(0,0,0,0.6)"
							optionsList={spoutTypes}
							onChange={(val) => customUpdateEvent("inventoryData.dispenseDetails.dispenserConfig.spoutType", val)}
							margin="0.5rem 0 0 0"
							value={objectToEdit?.inventoryData?.dispenseDetails?.dispenserConfig?.spoutType}
						/>
					</Div>
				</Div>
				<Div jContent="flex-end" margin="1rem 0 0 0">
					<Div margin="0.5rem" onClick={() => {}}>
						<Button onClick={() => navigate(`/studio/ingredient/${id}?action=noReload`)}>Cancel</Button>
					</Div>
					<Div margin="0.5rem">
						<SuccessButton
							onClick={() => {
								showConfirmModal(true);
							}}>
							Confirm
						</SuccessButton>
					</Div>
				</Div>
			</Card>
		);
	}

	return (
		<Page>
			<Div width="100%" jContent="space-between" alignItem="center">
				<span
					onClick={() => {
						navigate(`/studio/ingredient/${id}?action=noReload`);
					}}
					className="material-symbols-outlined"
					style={{ cursor: "pointer" }}>
					arrow_back
				</span>
			</Div>

			{renderIngredientEdit()}
			<CSSTransition in={confirmModal} timeout={1000} classNames="container" unmountOnExit>
				<Overlay>
					<Modal>
						<Div
							jContent="space-between"
							alignItem="center"
							color="#fff"
							width="100%"
							background="rgba(45, 35, 66, 0.8)"
							fontSize="1.7rem"
							padding="1rem">
							<Span>Save Changes</Span>
							<img
								onClick={() => {
									showConfirmModal(false);
								}}
								src={close}
								alt="close"
								style={{
									height: "30px",
									filter: "invert(97%) sepia(0%) saturate(7500%) hue-rotate(126deg) brightness(111%) contrast(100%)",
								}}
							/>
						</Div>
						{/* <Div overflowY="auto" flexDirection="column" padding="0rem 2rem 1rem 2rem">
							{renderChanges()}
						</Div> */}

						<Div jContent="flex-end" margin="1rem 0 0 0">
							<Div
								margin="0.5rem"
								onClick={() => {
									showConfirmModal(false);
								}}>
								<Button>Cancel</Button>
							</Div>
							<Div margin="0.5rem">
								<SuccessButton
									onClick={() => {
										sendUpdatedData();
									}}>
									Confirm
								</SuccessButton>
							</Div>
						</Div>
					</Modal>
				</Overlay>
			</CSSTransition>
		</Page>
	);
};

export default CookBookConnector(IngredientEdit);
